/**
 * OCR용 이미지 crop/warp 편집기.
 * 레거시 `OcrCropWarpUploader.js` 포팅.
 */
import { Button } from '@/shared/components/ui/button';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  createOcrReadyImage,
  loadImageElementFromFile,
  loadImageElementFromUrl,
  type Point2D,
  type PreprocessMode,
} from '@/shared/utils/ocrImagePreprocess';
import {
  createDefaultPoints,
  getBaseName,
  getBoundingBoxFromPoints,
  normalizePoints,
  reverseRotatePointsByQuarterTurns,
  rotatePointsByQuarterTurns,
} from '@/shared/utils/ocrCropWarpUtils';
import { useToast } from '@/shared/utils/toast';
import {
  Eye,
  Plus,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface OcrCropWarpFileItem {
  uid: string;
  name: string;
  status: 'done';
  type: string;
  size: number;
  preview?: string;
  originFileObj: File;
  ocrMeta?: {
    corners: Point2D[];
    bbox: { x: number; y: number; width: number; height: number };
  };
}

interface EditorFile {
  name: string;
  __sourceUrl?: string;
  __initialPoints?: Point2D[] | null;
}

/** 편집 중 선택 영역 — 적용(Apply) 없이도 OCR 요청에 쓸 수 있는 원본 좌표 */
export interface OcrCropWarpSelection {
  corners: Point2D[];
  bbox: { x: number; y: number; width: number; height: number };
  previewUrl?: string;
}

export interface OcrCropWarpUploaderProps {
  value?: OcrCropWarpFileItem[];
  onChange?: (items: OcrCropWarpFileItem[]) => void;
  maxCount?: number;
  multiple?: boolean;
  buttonText?: string;
  preprocessMode?: PreprocessMode;
  outputType?: string;
  outputQuality?: number;
  maxSourceEdge?: number;
  maxWarpWidth?: number;
  maxWarpHeight?: number;
  sourceImageUrl?: string | null;
  sourceInitialPoints?: Point2D[] | null;
  sourceImageName?: string;
  allowFileSelect?: boolean;
  showSourceButton?: boolean;
  sourceButtonText?: string;
  /** 꼭짓점 조정 시 원본 좌표·미리보기 URL을 부모에 전달 (적용 버튼 불필요) */
  onSelectionChange?: (selection: OcrCropWarpSelection | null) => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const rotateImage90 = (image: HTMLImageElement, clockwise = true) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.height;
      canvas.height = image.width;
      const ctx = canvas.getContext('2d')!;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((clockwise ? 90 : -90) * (Math.PI / 180));
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      const rotated = new Image();
      rotated.onload = () => resolve(rotated);
      rotated.onerror = reject;
      rotated.src = canvas.toDataURL('image/png');
    } catch (error) {
      reject(error);
    }
  });

export const OcrCropWarpUploader: React.FC<OcrCropWarpUploaderProps> = ({
  value,
  onChange,
  maxCount = 1,
  multiple = false,
  buttonText = 'OCR 이미지 선택',
  preprocessMode = 'warp',
  outputType = 'image/png',
  outputQuality = 0.92,
  maxSourceEdge = 2400,
  maxWarpWidth = 2400,
  maxWarpHeight = 3200,
  sourceImageUrl,
  sourceInitialPoints = null,
  sourceImageName = 'source-image.png',
  allowFileSelect = true,
  showSourceButton = true,
  sourceButtonText = '현재 이미지 편집',
  onSelectionChange,
}) => {
  const toast = useToast();
  const [internalList, setInternalList] = useState<OcrCropWarpFileItem[]>([]);
  const fileList = value ?? internalList;

  const setFileList = (next: OcrCropWarpFileItem[]) => {
    if (value === undefined) setInternalList(next);
    onChange?.(next);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewUrlRef = useRef('');

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editorFile, setEditorFile] = useState<EditorFile | null>(null);
  const [sourceImage, setSourceImage] = useState<HTMLImageElement | null>(null);
  const [editorImage, setEditorImage] = useState<HTMLImageElement | null>(null);
  const [points, setPoints] = useState<Point2D[]>([]);
  const [rotationQuarterTurns, setRotationQuarterTurns] = useState(0);
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [isDraggingAll, setIsDraggingAll] = useState(false);
  const dragAllStartRef = useRef<{
    canvasPoint: Point2D;
    points: Point2D[];
  } | null>(null);
  const [canvasMeta, setCanvasMeta] = useState({
    fitScale: 1,
    width: 0,
    height: 0,
  });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<OcrCropWarpFileItem | null>(
    null,
  );
  const [livePreviewUrl, setLivePreviewUrl] = useState('');

  const canSelectMore = fileList.length < maxCount;
  const hasSourceImage = Boolean(sourceImageUrl);
  // 처방 검수처럼 바깥 Dialog 안에서 쓸 때는 원본 버튼을 숨긴다.
  // 그때는 중첩 Dialog 대신 인라인 편집기를 바로 연다.
  const embedSourceEditor = !showSourceButton && hasSourceImage;
  const autoOpenedSourceRef = useRef(false);

  useEffect(() => {
    if (!editorFile && pendingFiles.length > 0) {
      const [next, ...rest] = pendingFiles;
      setPendingFiles(rest);
      setEditorFile({ name: next.name });
    }
  }, [pendingFiles, editorFile]);

  useEffect(() => {
    if (!embedSourceEditor || !sourceImageUrl || autoOpenedSourceRef.current) {
      return;
    }
    autoOpenedSourceRef.current = true;
    setEditorFile({
      name: sourceImageName,
      __sourceUrl: sourceImageUrl,
      __initialPoints: sourceInitialPoints,
    });
  }, [embedSourceEditor, sourceImageUrl, sourceImageName, sourceInitialPoints]);

  useEffect(() => {
    if (!editorFile) {
      setSourceImage(null);
      setEditorImage(null);
      setPoints([]);
      setRotationQuarterTurns(0);
      return;
    }

    let mounted = true;
    const loadPromise = editorFile.__sourceUrl
      ? loadImageElementFromUrl(editorFile.__sourceUrl)
      : loadImageElementFromFile(editorFile as unknown as File);

    loadPromise
      .then((img) => {
        if (!mounted) return;
        setSourceImage(img);
        setEditorImage(img);
        setRotationQuarterTurns(0);
        setPoints(
          normalizePoints(editorFile.__initialPoints ?? undefined, img.width, img.height) ||
            createDefaultPoints(img.width, img.height),
        );
      })
      .catch(() => {
        toast.error('이미지를 불러오지 못했습니다.');
        setEditorFile(null);
      });

    return () => {
      mounted = false;
    };
  }, [editorFile, toast]);

  useEffect(() => {
    if (!editorImage || !canvasRef.current) return;
    const maxDisplayWidth = 940;
    const maxDisplayHeight = 560;
    const fitScale = Math.min(
      maxDisplayWidth / editorImage.width,
      maxDisplayHeight / editorImage.height,
      1,
    );
    const width = Math.max(1, Math.round(editorImage.width * fitScale));
    const height = Math.max(1, Math.round(editorImage.height * fitScale));
    const canvas = canvasRef.current;
    canvas.width = width;
    canvas.height = height;
    setCanvasMeta({ fitScale, width, height });
  }, [editorImage]);

  const drawEditor = useCallback(() => {
    if (!editorImage || !canvasRef.current || points.length !== 4) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(editorImage, 0, 0, canvas.width, canvas.height);

    const scaled = points.map((p) => ({
      x: p.x * canvasMeta.fitScale,
      y: p.y * canvasMeta.fitScale,
    }));

    ctx.strokeStyle = '#1677ff';
    ctx.lineWidth = 2;
    ctx.fillStyle = 'rgba(22,119,255,0.18)';
    ctx.beginPath();
    ctx.moveTo(scaled[0].x, scaled[0].y);
    for (let i = 1; i < scaled.length; i += 1) {
      ctx.lineTo(scaled[i].x, scaled[i].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    scaled.forEach((p, index) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
      ctx.fillStyle = index === draggingIndex ? '#ff4d4f' : '#1677ff';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }, [editorImage, points, canvasMeta.fitScale, draggingIndex]);

  useEffect(() => {
    drawEditor();
  }, [drawEditor]);

  useEffect(() => {
    if (!editorImage || points.length !== 4) return;
    let cancelled = false;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setLivePreviewUrl('');

    const timer = setTimeout(async () => {
      try {
        const previewResult = await createOcrReadyImage({
          image: editorImage,
          points,
          preprocessMode,
          outputType,
          outputQuality,
          maxSourceEdge,
          maxWarpWidth,
          maxWarpHeight,
        });
        if (cancelled) return;
        const nextUrl = URL.createObjectURL(previewResult.blob);
        previewUrlRef.current = nextUrl;
        setLivePreviewUrl(nextUrl);
      } catch {
        // live preview failure is non-fatal
      }
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    editorImage,
    points,
    preprocessMode,
    outputType,
    outputQuality,
    maxSourceEdge,
    maxWarpWidth,
    maxWarpHeight,
  ]);

  // 적용 없이도 OCR 요청 가능하도록 원본 좌표계 선택 영역을 부모에 동기화
  useEffect(() => {
    if (!onSelectionChange) return;
    if (!editorFile || !editorImage || points.length !== 4) {
      onSelectionChange(null);
      return;
    }
    const processingPoints =
      sourceImage && rotationQuarterTurns !== 0
        ? reverseRotatePointsByQuarterTurns(
            points,
            editorImage.width,
            editorImage.height,
            rotationQuarterTurns,
          ) || points
        : points;
    const bbox = getBoundingBoxFromPoints(processingPoints) ?? {
      x: 0,
      y: 0,
      width: (sourceImage || editorImage).width,
      height: (sourceImage || editorImage).height,
    };
    onSelectionChange({
      corners: processingPoints,
      bbox,
      previewUrl: livePreviewUrl || undefined,
    });
  }, [
    onSelectionChange,
    editorFile,
    editorImage,
    sourceImage,
    points,
    rotationQuarterTurns,
    livePreviewUrl,
  ]);

  const getCanvasPoint = (event: React.MouseEvent<HTMLCanvasElement>): Point2D => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / Math.max(1, rect.width);
    const scaleY = canvas.height / Math.max(1, rect.height);
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const findHandleIndex = (canvasPoint: Point2D) => {
    const hitRadius = 12;
    let found = -1;
    points.forEach((point, index) => {
      const px = point.x * canvasMeta.fitScale;
      const py = point.y * canvasMeta.fitScale;
      if (Math.hypot(px - canvasPoint.x, py - canvasPoint.y) <= hitRadius) {
        found = index;
      }
    });
    return found;
  };

  const isPointInPolygon = (point: Point2D, polygon: Point2D[]) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;
      const intersect =
        yi > point.y !== yj > point.y &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + 1e-12) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const onCanvasMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!editorImage) return;
    const point = getCanvasPoint(event);
    const hitIndex = findHandleIndex(point);
    if (hitIndex >= 0) {
      setDraggingIndex(hitIndex);
      setIsDraggingAll(false);
      dragAllStartRef.current = null;
      return;
    }
    if (points.length === 4) {
      const polygon = points.map((p) => ({
        x: p.x * canvasMeta.fitScale,
        y: p.y * canvasMeta.fitScale,
      }));
      if (isPointInPolygon(point, polygon)) {
        setIsDraggingAll(true);
        dragAllStartRef.current = {
          canvasPoint: point,
          points: points.map((p) => ({ ...p })),
        };
      }
    }
  };

  const onCanvasMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!editorImage) return;
    const point = getCanvasPoint(event);

    if (isDraggingAll && dragAllStartRef.current) {
      const start = dragAllStartRef.current;
      const dx = (point.x - start.canvasPoint.x) / canvasMeta.fitScale;
      const dy = (point.y - start.canvasPoint.y) / canvasMeta.fitScale;
      const nextPoints = start.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
      const minX = Math.min(...nextPoints.map((p) => p.x));
      const minY = Math.min(...nextPoints.map((p) => p.y));
      const maxX = Math.max(...nextPoints.map((p) => p.x));
      const maxY = Math.max(...nextPoints.map((p) => p.y));
      let clampDx = 0;
      let clampDy = 0;
      if (minX < 0) clampDx = -minX;
      if (maxX > editorImage.width - 1)
        clampDx = Math.min(clampDx, editorImage.width - 1 - maxX);
      if (minY < 0) clampDy = -minY;
      if (maxY > editorImage.height - 1)
        clampDy = Math.min(clampDy, editorImage.height - 1 - maxY);
      setPoints(
        nextPoints.map((p) => ({ x: p.x + clampDx, y: p.y + clampDy })),
      );
      return;
    }

    if (draggingIndex < 0) return;
    const nextX = point.x / canvasMeta.fitScale;
    const nextY = point.y / canvasMeta.fitScale;
    setPoints((prev) =>
      prev.map((p, idx) =>
        idx === draggingIndex
          ? {
              x: Math.max(0, Math.min(editorImage.width - 1, nextX)),
              y: Math.max(0, Math.min(editorImage.height - 1, nextY)),
            }
          : p,
      ),
    );
  };

  const onCanvasMouseUp = () => {
    setDraggingIndex(-1);
    setIsDraggingAll(false);
    dragAllStartRef.current = null;
  };

  const openSourceImageEditor = () => {
    if (!sourceImageUrl) {
      toast.error('편집할 원본 이미지 URL이 없습니다.');
      return;
    }
    setEditorFile({
      name: sourceImageName,
      __sourceUrl: sourceImageUrl,
      __initialPoints: sourceInitialPoints,
    });
  };

  const onCancelEditor = () => {
    setEditorFile(null);
    setZoomLevel(1);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setLivePreviewUrl('');
  };

  const onResetPoints = () => {
    if (!editorImage) return;
    const resetPoints =
      sourceImage && editorFile?.__initialPoints
        ? rotatePointsByQuarterTurns(
            editorFile.__initialPoints,
            sourceImage.width,
            sourceImage.height,
            rotationQuarterTurns,
          )
        : null;
    setPoints(
      resetPoints || createDefaultPoints(editorImage.width, editorImage.height),
    );
  };

  const onRotateEditor = async (clockwise: boolean) => {
    if (!editorImage) return;
    try {
      const rotatedPoints =
        rotatePointsByQuarterTurns(
          points,
          editorImage.width,
          editorImage.height,
          clockwise ? 1 : 3,
        ) || createDefaultPoints(editorImage.height, editorImage.width);
      const rotated = await rotateImage90(editorImage, clockwise);
      setEditorImage(rotated);
      setPoints(rotatedPoints);
      setRotationQuarterTurns((prev) =>
        clockwise ? (prev + 1) % 4 : (prev + 3) % 4,
      );
    } catch {
      toast.error('이미지 회전에 실패했습니다.');
    }
  };

  const onApplyEditor = async () => {
    if (!editorFile || !editorImage || points.length !== 4) return;
    try {
      setIsProcessing(true);
      const processingImage = sourceImage || editorImage;
      const processingPoints =
        sourceImage && rotationQuarterTurns !== 0
          ? reverseRotatePointsByQuarterTurns(
              points,
              editorImage.width,
              editorImage.height,
              rotationQuarterTurns,
            ) || points
          : points;
      const { blob } = await createOcrReadyImage({
        image: processingImage,
        points: processingPoints,
        preprocessMode,
        outputType,
        outputQuality,
        maxSourceEdge,
        maxWarpWidth,
        maxWarpHeight,
      });

      const ext = outputType === 'image/jpeg' ? 'jpg' : 'png';
      const outputName = `${getBaseName(editorFile.name)}-ocr.${ext}`;
      const processedFile = new File([blob], outputName, { type: outputType });
      const previewUrl = URL.createObjectURL(processedFile);

      const nextItem: OcrCropWarpFileItem = {
        uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: processedFile.name,
        status: 'done',
        type: processedFile.type,
        size: processedFile.size,
        preview: previewUrl,
        originFileObj: processedFile,
        ocrMeta: {
          corners: processingPoints,
          bbox: getBoundingBoxFromPoints(processingPoints) ?? {
            x: 0,
            y: 0,
            width: processingImage.width,
            height: processingImage.height,
          },
        },
      };

      setFileList([...fileList, nextItem].slice(0, maxCount));
      onCancelEditor();
    } catch {
      toast.error('이미지 처리에 실패했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  const onSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    const remaining = Math.max(0, maxCount - fileList.length);
    const picked = imageFiles.slice(0, multiple ? remaining : 1);
    setPendingFiles((prev) => [...prev, ...picked]);
  };

  const onRemoveItem = (uid: string) => {
    const target = fileList.find((item) => item.uid === uid);
    if (target?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(target.preview);
    }
    setFileList(fileList.filter((item) => item.uid !== uid));
  };

  const modeHint = useMemo(() => {
    if (preprocessMode === 'crop') {
      return '점 4개를 드래그하면 선택 영역 기준으로 크로핑 이미지가 생성됩니다.';
    }
    if (preprocessMode === 'warp-crop') {
      return '점 4개를 드래그하면 원근 보정된 뒤 크로핑 이미지로 생성됩니다.';
    }
    return '점 4개를 드래그하면 선택 영역이 원근 보정(워핑)되어 생성됩니다.';
  }, [preprocessMode]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      fileList.forEach((item) => {
        if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const renderEditorWorkspace = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        꼭짓점 4개를 조정하면 선택 영역이 워핑됩니다.
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setZoomLevel((p) => Math.max(MIN_ZOOM, Number((p - 0.2).toFixed(2))))
          }
        >
          <ZoomOut className="mr-1 h-4 w-4" />
          축소
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setZoomLevel((p) => Math.min(MAX_ZOOM, Number((p + 0.2).toFixed(2))))
          }
        >
          <ZoomIn className="mr-1 h-4 w-4" />
          확대
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setZoomLevel(1)}
        >
          배율 초기화
        </Button>
        <span className="self-center text-sm text-muted-foreground">
          배율 {Math.round(zoomLevel * 100)}%
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void onRotateEditor(false)}
        >
          <RotateCcw className="mr-1 h-4 w-4" />
          왼쪽 90°
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void onRotateEditor(true)}
        >
          <RotateCw className="mr-1 h-4 w-4" />
          오른쪽 90°
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onResetPoints}>
          <RefreshCw className="mr-1 h-4 w-4" />
          선택 영역 초기화
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-sm font-medium">원본 (선택 영역 조정)</p>
          <div className="max-h-[560px] overflow-auto rounded-12 border bg-black">
            <canvas
              ref={canvasRef}
              onMouseDown={onCanvasMouseDown}
              onMouseMove={onCanvasMouseMove}
              onMouseUp={onCanvasMouseUp}
              onMouseLeave={onCanvasMouseUp}
              className="block cursor-crosshair"
              style={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'top left',
              }}
            />
          </div>
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">처리 결과 (실시간 비교)</p>
          <div className="flex min-h-[180px] items-center justify-center overflow-hidden rounded-12 border bg-muted/20">
            {livePreviewUrl ? (
              <img
                src={livePreviewUrl}
                alt="live-preview"
                className="max-h-[520px] max-w-full object-contain"
              />
            ) : (
              <span className="text-sm text-muted-foreground">
                처리 결과를 생성 중입니다...
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {allowFileSelect ? (
          <FileUploadButton
            inputRef={inputRef}
            accept="image/png,image/jpeg,image/jpg,image/webp"
            multiple={multiple}
            disabled={!canSelectMore}
            resetOnSelect={false}
            showUploadIcon={false}
            onChange={onSelectFiles}
          >
            <Plus />
            {buttonText}
          </FileUploadButton>
        ) : null}
        {hasSourceImage && showSourceButton ? (
          <Button type="button" onClick={openSourceImageEditor}>
            {sourceButtonText}
          </Button>
        ) : null}
      </div>

      {!(embedSourceEditor && editorFile) && (
        <p className="text-sm text-muted-foreground">{modeHint}</p>
      )}

      {fileList.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {fileList.map((item) => (
            <div key={item.uid} className="rounded-12 border p-2">
              <div className="mb-2 flex h-28 items-center justify-center overflow-hidden rounded bg-muted/30">
                {item.preview ? (
                  <img
                    src={item.preview}
                    alt={item.name}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">Preview 없음</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm">{item.name}</span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setPreviewTarget(item);
                      setPreviewOpen(true);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemoveItem(item.uid)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {embedSourceEditor && editorFile ? (
        <div className="space-y-4">
          <p className="text-sm font-medium">OCR 영역 선택</p>
          <p className="text-sm text-muted-foreground">
            영역을 조정한 뒤 바로 OCR을 요청할 수 있습니다. (적용 버튼 없이 가능)
          </p>
          {renderEditorWorkspace()}
        </div>
      ) : (
        <Dialog
          open={Boolean(editorFile)}
          onOpenChange={(open) => !open && onCancelEditor()}
        >
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>OCR 영역 선택</DialogTitle>
            </DialogHeader>
            {renderEditorWorkspace()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancelEditor}>
                건너뛰기
              </Button>
              <Button
                type="button"
                onClick={onApplyEditor}
                disabled={isProcessing}
              >
                {isProcessing ? '처리 중...' : '적용'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewTarget?.name || '처리 이미지 미리보기'}</DialogTitle>
          </DialogHeader>
          {previewTarget?.preview ? (
            <img
              src={previewTarget.preview}
              alt={previewTarget.name}
              className="mx-auto max-h-[700px] max-w-full object-contain"
            />
          ) : (
            <p className="text-sm text-muted-foreground">미리보기 이미지가 없습니다.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
